import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ProtectedRoute, CompanyAdminRoute, AdminRoute, PlatformAdminRoute } from './components/ProtectedRoute';
import { Landing } from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import CompanySettings from './pages/CompanySettings';
import CompanyManagement from './pages/CompanyManagement';
import CompanyDetail from './pages/CompanyDetail';
import UserManagement from './pages/UserManagement';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import { ProjectMetrics } from './pages/ProjectMetrics';
import Requirements from './pages/Requirements';
import { RequirementMetrics } from './pages/RequirementMetrics';
import AccountSettings from './pages/AccountSettings';
import { SystemHealth } from './pages/SystemHealth';
import AIConfigurationPage from './pages/AIConfiguration';
import JiraConfiguration from './pages/JiraConfiguration';
import JiraWebhookLogs from './pages/JiraWebhookLogs';
import { JiraIntegration } from './pages/JiraIntegration';
import { NewProjectWizard } from './pages/NewProjectWizard';
import GanttChart from './pages/GanttChart';
import CompanySetupWizard from './pages/CompanySetupWizard';
import { AIGeneratedRequirements } from './pages/AIGeneratedRequirements';
import UserLocationMap from './pages/UserLocationMap';

/**
 * Main App Component
 *
 * Sets up routing and authentication context for the application.
 */

function App(): React.ReactElement {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/company/settings"
            element={
              <CompanyAdminRoute>
                <CompanySettings />
              </CompanyAdminRoute>
            }
          />

          <Route
            path="/company/jira"
            element={
              <CompanyAdminRoute>
                <JiraConfiguration />
              </CompanyAdminRoute>
            }
          />

          <Route
            path="/setup/company"
            element={
              <ProtectedRoute>
                <CompanySetupWizard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/users"
            element={
              <AdminRoute>
                <UserManagement />
              </AdminRoute>
            }
          />

          <Route
            path="/projects"
            element={
              <ProtectedRoute>
                <Projects />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects/new"
            element={
              <ProtectedRoute>
                <NewProjectWizard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects/:id"
            element={
              <ProtectedRoute>
                <ProjectDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects/:id/metrics"
            element={
              <ProtectedRoute>
                <ProjectMetrics />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects/:projectId/jira"
            element={
              <ProtectedRoute>
                <JiraIntegration />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects/:projectId/gantt"
            element={
              <ProtectedRoute>
                <GanttChart />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects/:projectId/requirements"
            element={
              <ProtectedRoute>
                <Requirements />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects/:projectId/ai-requirements"
            element={
              <ProtectedRoute>
                <AIGeneratedRequirements />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects/:projectId/requirements/metrics"
            element={
              <ProtectedRoute>
                <RequirementMetrics />
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings/account"
            element={
              <ProtectedRoute>
                <AccountSettings />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/system-health"
            element={
              <AdminRoute>
                <SystemHealth />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/ai-config"
            element={
              <PlatformAdminRoute>
                <AIConfigurationPage />
              </PlatformAdminRoute>
            }
          />

          <Route
            path="/admin/jira-webhooks"
            element={
              <PlatformAdminRoute>
                <JiraWebhookLogs />
              </PlatformAdminRoute>
            }
          />

          <Route
            path="/admin/user-location-map"
            element={
              <PlatformAdminRoute>
                <UserLocationMap />
              </PlatformAdminRoute>
            }
          />

          <Route
            path="/admin/companies"
            element={
              <ProtectedRoute>
                <CompanyManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/companies/:id"
            element={
              <ProtectedRoute>
                <CompanyDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/companies/:id/edit"
            element={
              <ProtectedRoute>
                <CompanyDetail />
              </ProtectedRoute>
            }
          />

          {/* Catch-all redirect to landing page */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
